import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { IconCircleCheckOutline18, IconRefreshOutline18 } from "nucleo-ui-outline-18";
import { useLatest } from "@/hooks/useLatest";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchWorksheetTabs, parseGoogleSheetInput, type WorksheetTab } from "./googleSheets";
export interface GoogleSheetInputProps {
  label: string;
  description?: string;
  headerRight?: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  connectorId?: string;
  connectorType?: string;
  onValidationStateChange?: (valid: boolean) => void;
}
export default function GoogleSheetInput({
  label,
  description,
  headerRight,
  value,
  onChange,
  placeholder = "https://docs.google.com/spreadsheets/d/... or spreadsheet ID",
  required = false,
  connectorId,
  connectorType,
  onValidationStateChange
}: GoogleSheetInputProps) {
  const [draft, setDraft] = useState((value ?? "").toString());
  const committedRef = useRef(value ?? "");
  useEffect(() => {
    const external = (value ?? "").toString();
    if (external !== committedRef.current) {
      committedRef.current = external;
      setDraft(external);
    }
  }, [value]);
  const parsed = useMemo(() => parseGoogleSheetInput(draft), [draft]);
  const hasInput = draft.trim().length > 0;
  const isValid = !hasInput || parsed !== null;
  const [sheets, setSheets] = useState<WorksheetTab[] | null>(null);
  const [spreadsheetTitle, setSpreadsheetTitle] = useState<string | null>(null);
  const [fetchStatus, setFetchStatus] = useState<"idle" | "loading" | "error">("idle");
  const [isEditing, setIsEditing] = useState(false);
  const lastFetchedId = useRef<string>("");
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [debouncing, setDebouncing] = useState(false);
  useEffect(() => {
    lastFetchedId.current = "";
    setSheets(null);
    setSpreadsheetTitle(null);
    setFetchStatus("idle");
    setHasFetchedOnce(false);
  }, [connectorId, connectorType]);
  const commitValue = useCallback((val: string) => {
    committedRef.current = val;
    onChange(val);
  }, [onChange]);
  const fetchSheets = useCallback(async (rawValue: string) => {
    if (!connectorId || !connectorType) return;
    if (lastFetchedId.current === rawValue) return;
    lastFetchedId.current = rawValue;
    setFetchStatus("loading");
    setSheets(null);
    setSpreadsheetTitle(null);
    const result = await fetchWorksheetTabs(connectorId, connectorType, rawValue);
    if (lastFetchedId.current !== rawValue) return;
    setHasFetchedOnce(true);
    if (result.status === "found") {
      setSheets(result.sheets);
      setSpreadsheetTitle(result.spreadsheetTitle);
      setFetchStatus("idle");
      setIsEditing(false);
      commitValue(rawValue);
    } else {
      setFetchStatus("error");
    }
  }, [connectorId, connectorType, commitValue]);
  const fetchSheetsRef = useLatest(fetchSheets);
  useEffect(() => {
    if (!parsed) {
      setDebouncing(false);
      if (lastFetchedId.current !== "") {
        setSheets(null);
        setSpreadsheetTitle(null);
        setFetchStatus("idle");
        lastFetchedId.current = "";
      }
      return;
    }
    setDebouncing(true);
    setSheets(null);
    setSpreadsheetTitle(null);
    const timer = setTimeout(() => {
      setDebouncing(false);
      fetchSheetsRef.current(draft.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [parsed, draft, connectorId, connectorType]);
  const handleValidateClick = useCallback(() => {
    if (!parsed || !connectorId || !connectorType) return;
    lastFetchedId.current = "";
    fetchSheetsRef.current(draft.trim());
  }, [parsed, connectorId, connectorType, draft]);
  const sheetSummary = useMemo(() => {
    if (!sheets || sheets.length === 0) return null;
    if (sheets.length <= 3) return sheets.map(s => s.title).join(", ");
    return `${sheets[0].title}, ${sheets[1].title} +${sheets.length - 2} more`;
  }, [sheets]);
  const isResolved = parsed && sheets && !isEditing;
  const handleEditClick = () => {
    setIsEditing(true);
  };
  const handleBlur = () => {
    setIsEditing(false);
  };
  const handleClear = useCallback(() => {
    setDraft("");
    setSheets(null);
    setSpreadsheetTitle(null);
    setFetchStatus("idle");
    lastFetchedId.current = "";
    commitValue("");
  }, [commitValue]);
  const showValidateButton = parsed && fetchStatus === "idle" && !debouncing && !sheets && connectorId && connectorType && hasFetchedOnce;
  const isValidated = !!(parsed && sheets);
  useEffect(() => {
    onValidationStateChange?.(isValidated);
  }, [isValidated, onValidationStateChange]);
  return <div className="space-y-2">
      {label && <div className="flex items-center justify-between gap-3">
          <label className="field-label block">
            {label}
            {required && <span className="text-ds-error-text ml-1">*</span>}
          </label>
          {headerRight}
        </div>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}

      {isResolved ? <button type="button" className="bg-ds-surface-card dark:hover:bg-ds-neutral-700 hover:bg-ds-surface-hover border-ds-success-border flex w-full cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors" onClick={handleEditClick}>
          {/* Google Sheets brand green */}
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#0F9D58]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
              <path d="M19 11V9h-5V3H7.5L3 7.5V21h8v-2H5V9h4V5h4v4h2v2h4zm-8 4h8v2h-8v-2zm0 4h5v2h-5v-2z" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-ds-text-primary truncate font-medium">
                {spreadsheetTitle || "Google Spreadsheet"}
              </span>
            </div>
            {sheetSummary && <p className="text-ds-text-subtle dark:text-ds-text-disabled mt-0.5 text-xs">{sheetSummary}</p>}
          </div>
          <IconCircleCheckOutline18 className="text-ds-success-text h-4 w-4 flex-shrink-0" />
        </button> : <>
          <Input type="text" value={draft} onChange={e => {
        setDraft(e.target.value);
        setIsEditing(true);
      }} onBlur={handleBlur} placeholder={placeholder} className={`text-ds-text-primary dark:text-white ${!isValid ? "border-ds-error-border focus-visible:ring-ds-error-border/20" : ""}`} />
          {!isValid && <p className="micro-label text-ds-error-text">Enter a valid Google Sheets URL or spreadsheet ID.</p>}
          {hasInput && !parsed && <Button type="button" variant="white" onClick={handleClear} className="h-7 gap-1.5 px-2.5 text-xs">
              Clear
            </Button>}
          {parsed && fetchStatus === "loading" && <p className="text-ds-info-text flex items-center gap-1.5 text-xs">
              <PixelLoader size="xs" /> Validating spreadsheet...
            </p>}
          {parsed && fetchStatus === "error" && <div className="space-y-2">
              <p className="text-ds-warning-text text-xs">
                Could not access this spreadsheet. Check the URL and make sure it&apos;s shared with your connected
                Google account.
              </p>
              <Button type="button" variant="white" onClick={handleValidateClick} className="h-7 gap-1.5 px-2.5 text-xs">
                <IconRefreshOutline18 className="h-3.5 w-3.5" />
                Retry
              </Button>
            </div>}
          {showValidateButton && <Button type="button" variant="white" onClick={handleValidateClick} className="h-7 gap-1.5 px-2.5 text-xs">
              <IconCircleCheckOutline18 className="h-3.5 w-3.5" />
              Validate Access
            </Button>}
          {parsed && !connectorId && fetchStatus === "idle" && <p className="text-ds-text-muted text-xs">
              Connect your Google account to validate access to this spreadsheet.
            </p>}
        </>}
    </div>;
}